import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button, Flex } from 'antd';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import {
  CategoryFormDataType,
  CategoryHierarchyValidFormDataType,
  CategoryValidFormDataType,
  ClassifierValidFormDataType,
  DragTableRowButton,
  ExpandTableRowButton,
  FormTableSequenceNumberField,
  getFormTableFieldName,
  ROW_UNIQUE_KEY_NAME,
  RowParentIdSelect,
  Table,
  TextEllipsis,
  UUIDType,
  useChangeTableRowPosition,
  FormField,
  FormInputMessages,
  useGetIdFromName,
  TableRowPositionChangeHandler,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './CategoryHierarchyFormTable.translations.json';
import { useSyncHierarchy } from './useSyncHierarchy';

const { addTranslations, useTranslation } = i18nNsFactory('category-hierarchy');
addTranslations(translations);

const columnHelper = createColumnHelper<CategoryHierarchyValidFormDataType>();
const tableName = 'hierarchy' as const;

export const CategoryHierarchyFormTable: FC = () => {
  const { t } = useTranslation();
  const form = useFormContext<ClassifierValidFormDataType>();
  const { getValues, setValue } = form;

  const getCategories = useCallback(
    () => getValues('categories') as CategoryFormDataType[],
    [getValues]
  );

  // We memoize this map beforehand for the duration of this component’s lifespan because we know that while user is editing hierarchy,
  // they cannot be adding/deleting any categories — the same categories will exist throughout the lifespan of this component and
  // getCategories always returns the same categories with same keys at same indexes.
  const categoryKeyToIndexMap = useMemo(() => {
    const map: { [key: UUIDType]: number } = {};
    const categoryEntries = getCategories().entries();

    for (const [index, { [ROW_UNIQUE_KEY_NAME]: key }] of categoryEntries) {
      map[key] = index;
    }

    return map;
  }, [getCategories]);

  const updateCategory = useCallback(
    (
      rowKey: UUIDType,
      updater: (current: CategoryValidFormDataType) => CategoryValidFormDataType
    ) => {
      const categories = getCategories();
      const categoryIndex = categoryKeyToIndexMap[rowKey];
      setValue(
        `categories.${categoryIndex}`,
        updater(categories[categoryIndex]),
        { shouldDirty: true }
      );
    },
    [setValue, getCategories, categoryKeyToIndexMap]
  );

  const updateParentKey = useCallback(
    (rowKey: UUIDType, parentKey: UUIDType | null, levelNumber: number) =>
      updateCategory(rowKey, (category) => ({
        ...category,
        parentKey,
        levelNumber,
      })),
    [updateCategory]
  );

  const updateChildSequenceNumber = useCallback(
    (rowKey: UUIDType, childSequenceNumber: number) =>
      updateCategory(rowKey, (category) => ({
        ...category,
        childSequenceNumber,
      })),
    [updateCategory]
  );

  const handleChildSequenceNumberRangeChange = useCallback(
    (parentPath: string, first: number, last?: number) => {
      getValues<any>(parentPath)
        .slice(first, last && last + 1)
        .forEach(({ [ROW_UNIQUE_KEY_NAME]: key, childSequenceNumber }: any) => {
          updateChildSequenceNumber(key, childSequenceNumber);
        });
    },
    [getValues, updateChildSequenceNumber]
  );

  const changeTableRowPosition = useChangeTableRowPosition(
    tableName,
    form,
    updateParentKey
  );

  // Sync categories -> hierarchy on mount for user may have changed the categories
  const syncHierarchy = useSyncHierarchy();
  useEffect(() => syncHierarchy(), [syncHierarchy]);

  const data = useWatch({ name: tableName });
  const getIdFromName = useGetIdFromName();

  if (!data) {
    return null;
  }

  return (
    <Flex vertical gap="middle" align="end" style={{ width: 'fit-content' }}>
      <Table
        data={data}
        name={tableName}
        rowKey={ROW_UNIQUE_KEY_NAME}
        actions={(table) => (
          <>
            <Button
              id="expand-all"
              onClick={() => table.toggleAllRowsExpanded(true)}
            >
              {t('button.expand-all')}
            </Button>
            <Button
              id="collapse-all"
              onClick={() => table.toggleAllRowsExpanded(false)}
            >
              {t('button.collapse-all')}
            </Button>
          </>
        )}
        columns={[
          columnHelper.accessor('drag' as any, {
            header: '',
            cell: (props) => (
              <DragTableRowButton
                {...props}
                onChangePosition={changeTableRowPosition}
              />
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              width: 48,
              className: 'sticky-left',
            },
          }),
          columnHelper.accessor('displayName', {
            header: t('column.displayName'),
            cell: DisplayNameCell,
            enableSorting: false,
            meta: {
              width: 350,
            },
          }),
          columnHelper.accessor('childSequenceNumber', {
            header: t('column.childSequenceNumber'),
            cell: (props) => (
              <FormTableSequenceNumberField
                {...props}
                onRangeChange={handleChildSequenceNumberRangeChange}
              />
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              width: 100,
              textAlign: 'right',
            },
          }),
          columnHelper.accessor('parent' as any, {
            header: t('column.parent'),
            cell: (props) => (
              <ParentCell
                {...props}
                getIdFromName={getIdFromName}
                changeTableRowPosition={changeTableRowPosition}
              />
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              minWidth: 200,
            },
          }),
        ]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
};

const DisplayNameCell: FC<
  CellContext<CategoryHierarchyValidFormDataType, any>
> = (props) => (
  <ExpandTableRowButton {...props}>
    <Flex gap="small" vertical style={{ minWidth: 0 }}>
      <Flex gap="small" style={{ minWidth: 0 }}>
        <TextEllipsis>{props.renderValue()}</TextEllipsis>
        {props.row.subRows.length > 0 && `(${props.row.subRows.length})`}
      </Flex>
      <FormInputMessages name={getFormTableFieldName(props)} />
    </Flex>
  </ExpandTableRowButton>
);

const ParentCell: FC<
  CellContext<CategoryHierarchyValidFormDataType, any> & {
    getIdFromName: (name: string) => string;
    changeTableRowPosition: TableRowPositionChangeHandler;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: () => props.row.getParentRow()?.original.displayName,
      }}
      showValidationMessages={false}
    >
      <RowParentIdSelect
        {...props}
        id={props.getIdFromName(name)}
        onSelect={props.changeTableRowPosition}
      />
    </FormField>
  );
});
